import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Calendar as CalendarIcon, Clock, MapPin, FileText, 
  DollarSign, ArrowRight, ArrowLeft, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import Navbar from "../components/Navbar";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { format } from "date-fns";

const eventTypes = [
  "Wedding",
  "Birthday Party",
  "Corporate Event",
  "Anniversary",
  "Graduation",
  "Baby Shower",
  "Holiday Party",
  "Other"
];

const BookingFlow = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    event_date: null,
    event_time: "",
    event_type: "",
    event_location: "",
    duration_hours: 2,
    notes: ""
  });

  useEffect(() => {
    fetchVendor();
  }, [vendorId]);

  const fetchVendor = async () => {
    try {
      const response = await axios.get(`${API}/vendors/${vendorId}`);
      setVendor(response.data);
    } catch (error) {
      console.error("Error fetching vendor:", error);
      toast.error("Vendor not found");
      navigate("/vendors");
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = vendor ? vendor.hourly_rate * formData.duration_hours : 0;
  const depositAmount = totalAmount * 0.25;

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/bookings`, {
        vendor_id: vendorId,
        event_date: format(formData.event_date, "yyyy-MM-dd"),
        event_time: formData.event_time,
        event_type: formData.event_type,
        event_location: formData.event_location,
        duration_hours: formData.duration_hours,
        notes: formData.notes
      }, { withCredentials: true });
      
      toast.success("Booking created! Please sign the contract.");
      navigate(`/contract/${response.data.booking_id}`);
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error("Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const isStep1Valid = formData.event_date && formData.event_time && formData.event_type;
  const isStep2Valid = formData.event_location && formData.duration_hours > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      <div className="pt-28 pb-12 px-6">
        <div className="container mx-auto max-w-3xl">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s ? "bg-gold text-black" : "bg-white/10 text-white/40"
                  }`}
                >
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-0.5 ${step > s ? "bg-gold" : "bg-white/10"}`}></div>
                )}
              </div>
            ))}
          </div>
          
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-charcoal rounded-2xl border border-white/5 p-8"
          >
            {/* Step 1: Event Details */}
            {step === 1 && (
              <>
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  Event Details
                </h2>
                <p className="text-white/60 mb-8">
                  Tell us about your event with {vendor?.business_name}
                </p>
                
                <div className="space-y-6">
                  <div>
                    <Label className="text-white/60">Event Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal mt-2 h-12 bg-obsidian border-white/10 text-white"
                          data-testid="event-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                          {formData.event_date ? (
                            format(formData.event_date, "PPP")
                          ) : (
                            <span className="text-white/40">Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-charcoal border-white/10" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.event_date}
                          onSelect={(date) => setFormData({ ...formData, event_date: date })}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="bg-charcoal text-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div>
                    <Label className="text-white/60">Event Time</Label>
                    <Input
                      type="time"
                      value={formData.event_time}
                      onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                      className="mt-2 h-12 bg-obsidian border-white/10 text-white"
                      data-testid="event-time-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/60">Event Type</Label>
                    <Select 
                      value={formData.event_type}
                      onValueChange={(v) => setFormData({ ...formData, event_type: v })}
                    >
                      <SelectTrigger className="mt-2 h-12 bg-obsidian border-white/10 text-white" data-testid="event-type-select">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent className="bg-charcoal border-white/10">
                        {eventTypes.map((type) => (
                          <SelectItem key={type} value={type} className="text-white hover:bg-white/5">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-end mt-8">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!isStep1Valid}
                    className="bg-gold text-black hover:bg-gold-light rounded-full font-bold"
                    data-testid="next-step-1"
                  >
                    Continue
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
            
            {/* Step 2: Location & Duration */}
            {step === 2 && (
              <>
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  Location & Duration
                </h2>
                <p className="text-white/60 mb-8">
                  Where and how long do you need our services?
                </p>
                
                <div className="space-y-6">
                  <div>
                    <Label className="text-white/60">Event Location</Label>
                    <div className="relative mt-2">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gold/60" />
                      <Input
                        value={formData.event_location}
                        onChange={(e) => setFormData({ ...formData, event_location: e.target.value })}
                        placeholder="Full address of the event"
                        className="pl-12 h-12 bg-obsidian border-white/10 text-white"
                        data-testid="event-location-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/60">Duration (hours)</Label>
                    <Select 
                      value={String(formData.duration_hours)}
                      onValueChange={(v) => setFormData({ ...formData, duration_hours: parseInt(v) })}
                    >
                      <SelectTrigger className="mt-2 h-12 bg-obsidian border-white/10 text-white" data-testid="duration-select">
                        <Clock className="w-4 h-4 mr-2 text-gold/60" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-charcoal border-white/10">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
                          <SelectItem key={h} value={String(h)} className="text-white hover:bg-white/5">
                            {h} {h === 1 ? "hour" : "hours"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-white/60">Special Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any special requests or requirements..."
                      rows={3}
                      className="mt-2 bg-obsidian border-white/10 text-white"
                      data-testid="notes-input"
                    />
                  </div>
                </div>
                
                <div className="flex justify-between mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="border-white/10 text-white hover:bg-white/5 rounded-full"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!isStep2Valid}
                    className="bg-gold text-black hover:bg-gold-light rounded-full font-bold"
                    data-testid="next-step-2"
                  >
                    Continue
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
            
            {/* Step 3: Review & Confirm */}
            {step === 3 && (
              <>
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  Review & Confirm
                </h2>
                <p className="text-white/60 mb-8">
                  Please review your booking details
                </p>
                
                <div className="bg-obsidian rounded-xl p-6 border border-white/5 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-white/60">Vendor</span>
                    <span className="text-white font-medium">{vendor?.business_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Event Date</span>
                    <span className="text-white">{formData.event_date && format(formData.event_date, "PPP")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Event Time</span>
                    <span className="text-white">{formData.event_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Event Type</span>
                    <span className="text-white">{formData.event_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Location</span>
                    <span className="text-white">{formData.event_location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Duration</span>
                    <span className="text-white">{formData.duration_hours} hours</span>
                  </div>
                  <div className="border-t border-white/5 pt-4">
                    <div className="flex justify-between">
                      <span className="text-white/60">Hourly Rate</span>
                      <span className="text-white">${vendor?.hourly_rate}/hr</span>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-white font-medium">Total Amount</span>
                      <span className="text-gold font-bold text-xl">${totalAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-white/60">Deposit Required (25%)</span>
                      <span className="text-white">${depositAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-6">
                  <p className="text-gold text-sm">
                    <FileText className="w-4 h-4 inline mr-2" />
                    After confirming, you'll need to sign a contract and pay the deposit to secure your booking.
                  </p>
                </div>
                
                <div className="flex justify-between mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="border-white/10 text-white hover:bg-white/5 rounded-full"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-gold text-black hover:bg-gold-light rounded-full font-bold gold-glow"
                    data-testid="confirm-booking-btn"
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        Confirm Booking
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;
